import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import {
  portalLine,
  portalSkin,
  type PortalAppearance,
  type PortalThemeRoles,
} from "@shared/consumer-tvod";
// The portal's typefaces, so the miniature sets the pairing the portal will.
// Self-hosted woff2, fetched only when a face is used. Relative, not
// "@shared/...": the standalone CMS lane aliases @shared to tsc output.
import "../../../shared/fonts/scheme.css";

/**
 * A miniature painted with the portal's own `portalSkin` properties. Placeholder
 * film names; the sample text follows the portal's language, not the CMS's.
 */

const SAMPLE = {
  sv: {
    search: "Sök på titel",
    nav: ["Filmer", "Mitt innehåll"],
    cta: "Skapa konto",
    headline: "Hyr film",
    heroCta: "Se erbjudandet",
    rail: "Populärt just nu",
    chips: ["Drama", "Dokumentär", "Komedi", "Klassiker"],
    from: "Från 49 kr",
    rented: "Hyrd",
    films: ["Nattåget", "Sommarön", "Älvdalen", "Över gränsen"],
    meta: [
      "2021 · 1 h 42 min",
      "2019 · 1 h 31 min",
      "2023 · 58 min",
      "2018 · 2 h 4 min",
    ],
  },
  en: {
    search: "Search by title",
    nav: ["Films", "My content"],
    cta: "Create account",
    headline: "Rent films",
    heroCta: "See the offer",
    rail: "Popular right now",
    chips: ["Drama", "Documentary", "Comedy", "Classics"],
    from: "From SEK 49",
    rented: "Rented",
    films: ["Night train", "Summer isle", "Älvdalen", "Over the border"],
    meta: [
      "2021 · 1 h 42 min",
      "2019 · 1 h 31 min",
      "2023 · 58 min",
      "2018 · 2 h 4 min",
    ],
  },
} as const;

/** Artwork stand-ins: tonal washes of the brand, never stock imagery. */
const WASHES = [
  "linear-gradient(135deg, rgb(var(--accent-rgb) / .85), rgb(var(--ink-rgb) / .9))",
  "linear-gradient(160deg, rgb(var(--ink-rgb) / .75), rgb(var(--accent-rgb) / .55))",
  "linear-gradient(120deg, rgb(var(--accent-rgb) / .45), rgb(var(--ink-rgb) / .8))",
  "linear-gradient(200deg, rgb(var(--ink-rgb) / .85), rgb(var(--accent-rgb) / .35))",
];

export default function PortalAppearancePreview({
  roles,
  appearance,
  logoUrl,
  name,
  lang,
}: {
  roles: PortalThemeRoles;
  appearance: PortalAppearance;
  logoUrl: string | null;
  name: string;
  lang: "sv" | "en";
}) {
  const { t } = useTranslation("offers");
  const skin = portalSkin(roles, appearance);
  const copy = SAMPLE[lang];
  const headline = portalLine(appearance.heroHeadline, lang) || copy.headline;
  const subline = portalLine(appearance.heroSubline, lang);
  const tagline = portalLine(appearance.tagline, lang);
  const hero = appearance.heroImageUrl ?? null;
  const compact = appearance.density === "compact";

  const style = {
    ...skin.vars,
    colorScheme: skin.colorScheme,
  } as CSSProperties;

  const display: CSSProperties = {
    fontFamily: "var(--display)",
    fontWeight: "var(--display-weight, 700)" as unknown as number,
    letterSpacing: "var(--display-tracking, -0.01em)",
  };

  return (
    <Box
      role="region"
      aria-label={t("portalAppearancePreview.region")}
      data-testid="appearance-preview"
      data-font={appearance.font ?? "template"}
      data-corners={appearance.corners ?? "rounded"}
      data-density={appearance.density ?? "comfortable"}
      style={style}
      sx={{
        // Defaults mirror src/portal/styles.css :root, so an unset role
        // falls back the way the portal's does.
        "--radius": "14px",
        "--radius-sm": "10px",
        "--radius-pill": "999px",
        "--display-scale": "1",
        bgcolor: "var(--paper)",
        color: "var(--ink)",
        fontFamily: "var(--sans)",
        borderRadius: "12px",
        border: "1px solid rgb(0 0 0 / .12)",
        overflow: "hidden",
        fontSize: 11,
        lineHeight: 1.45,
        userSelect: "none",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: "1px solid var(--hairline)",
        }}
      >
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt=""
            data-testid="preview-logo"
            sx={{ maxHeight: 20, maxWidth: 90, display: "block" }}
          />
        ) : (
          <Box
            data-testid="preview-name"
            sx={{ ...display, fontSize: 13, whiteSpace: "nowrap" }}
          >
            {name}
          </Box>
        )}
        <Box
          sx={{
            flex: 1,
            mx: 1,
            px: 1,
            py: 0.4,
            borderRadius: "var(--radius-pill)",
            border: "1px solid var(--hairline)",
            bgcolor: "var(--surface)",
            color: "var(--ink-muted)",
            fontSize: 10,
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          {`⌕ ${copy.search}`}
        </Box>
        <Box
          sx={{ color: "var(--ink-soft)", fontSize: 10, whiteSpace: "nowrap" }}
        >
          {copy.nav[0]}
        </Box>
        <Box
          sx={{
            px: 1,
            py: 0.4,
            borderRadius: "var(--radius-pill)",
            bgcolor: "var(--accent)",
            color: "var(--on-accent)",
            fontWeight: 600,
            fontSize: 10,
            whiteSpace: "nowrap",
          }}
        >
          {copy.cta}
        </Box>
      </Box>

      {/* Hero */}
      <Box
        data-testid="preview-hero"
        data-has-image={hero ? "true" : "false"}
        sx={{
          position: "relative",
          minHeight: 150,
          display: "flex",
          alignItems: "flex-end",
          backgroundColor: "var(--surface-alt)",
          backgroundImage: hero
            ? `url("${hero}")`
            : "radial-gradient(120% 90% at 85% 10%, rgb(var(--accent-rgb) / .35), transparent 60%), radial-gradient(80% 80% at 10% 100%, rgb(var(--accent-rgb) / .18), transparent 70%)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {hero && (
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to top, rgb(var(--paper-rgb) / 1) 4%, rgb(var(--paper-rgb) / .82) 38%, rgb(var(--paper-rgb) / .1) 85%), linear-gradient(to right, rgb(var(--paper-rgb) / .85), transparent 70%)",
            }}
          />
        )}
        <Box sx={{ position: "relative", p: 1.75, maxWidth: "85%" }}>
          {tagline && (
            <Box
              data-testid="preview-tagline"
              sx={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: ".1em",
                textTransform: "uppercase",
                color: "var(--accent-ink)",
                mb: 0.5,
              }}
            >
              {tagline}
            </Box>
          )}
          <Box
            data-testid="preview-headline"
            sx={{
              ...display,
              fontSize: "calc(22px * var(--display-scale))",
              lineHeight: 1.12,
            }}
          >
            {headline}
          </Box>
          {subline && (
            <Box
              data-testid="preview-subline"
              sx={{ color: "var(--ink-soft)", mt: 0.5, fontSize: 11 }}
            >
              {subline}
            </Box>
          )}
          <Box
            sx={{
              display: "inline-block",
              mt: 1,
              px: 1.25,
              py: 0.5,
              borderRadius: "var(--radius-sm)",
              bgcolor: "var(--accent)",
              color: "var(--on-accent)",
              fontWeight: 600,
              fontSize: 10.5,
            }}
          >
            {copy.heroCta}
          </Box>
        </Box>
      </Box>

      {/* Genre chips */}
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          px: 1.5,
          pt: 1.25,
          overflow: "hidden",
        }}
      >
        {copy.chips.map((c, i) => (
          <Box
            key={c}
            sx={{
              px: 1,
              py: 0.35,
              borderRadius: "var(--radius-pill)",
              border: "1px solid var(--hairline)",
              bgcolor: i === 0 ? "var(--ink)" : "var(--surface)",
              color: i === 0 ? "var(--paper)" : "var(--ink)",
              fontWeight: 600,
              fontSize: 9.5,
              whiteSpace: "nowrap",
            }}
          >
            {c}
          </Box>
        ))}
      </Box>

      {/* Rail */}
      <Box sx={{ px: 1.5, pt: 1.25, pb: 1.5 }}>
        <Box
          sx={{
            ...display,
            fontSize: "calc(13px * var(--display-scale))",
            mb: 0.75,
          }}
        >
          {copy.rail}
        </Box>
        <Box
          data-testid="preview-rail"
          sx={{
            display: "grid",
            gridAutoFlow: "column",
            gridAutoColumns: compact ? "29%" : "40%",
            gap: compact ? 0.75 : 1,
            overflow: "hidden",
          }}
        >
          {copy.films.map((film, i) => (
            <Box key={film}>
              <Box
                sx={{
                  position: "relative",
                  aspectRatio: "16 / 9",
                  borderRadius: "var(--radius-sm)",
                  background: WASHES[i % WASHES.length],
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: 4,
                    top: 4,
                    px: 0.6,
                    borderRadius: "var(--radius-pill)",
                    fontSize: 8.5,
                    fontWeight: 700,
                    bgcolor:
                      i === 1 ? "var(--accent)" : "rgb(var(--paper-rgb) / .9)",
                    color: i === 1 ? "var(--on-accent)" : "var(--ink)",
                  }}
                >
                  {i === 1 ? copy.rented : copy.from}
                </Box>
              </Box>
              <Box
                sx={{
                  fontWeight: 600,
                  mt: 0.5,
                  fontSize: 10.5,
                  lineHeight: 1.3,
                }}
              >
                {film}
              </Box>
              <Box sx={{ color: "var(--ink-muted)", fontSize: 9 }}>
                {copy.meta[i]}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
